﻿// #region: Class definition *********************************************************************/
/**
 * Manages core javascript content, needed everywhere.
 * The manifest for core.js includes/needs:
 *      broadcasts.js
 *      event-broadcaster.js
 *      messages.js
 *      applicationHub.js
 *      toast.js
 *      core.js (this file)
 *      validator.js
 *      validation.js
 * 
 * Depends on the 'toast' component (load before core.js).
 * 
 * Date/time formatting inspired on day.js, see: https://day.js.org/
 */
window.core = new class {

    // #region: Private Fields *******************************************************************/
    #rem;
    #toast;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * The constructor does work that needs to be executed exactly once.
     */
    constructor() {
        this.#toast = new toast();

        // Magic numbers.
        this.BREAKPOINT = 928;
        this.CALLBACK_TIMEOUT = 2500;
        this.CLEANUP_URL_TIMEOUT = 100;

        // iOS PWA detection.
        const isIos = /iPad|iPhone|iPod/.test(window.navigator.platform) || (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
        this.isIosPWA = isIos && "standalone" in window.navigator && window.navigator.standalone;

        if (isIos) {
            document.documentElement.classList.add('is-ios');
        }

        // We assume iOS 13+.
        if (this.isIosPWA && window.visualViewport) {
            const viewport = document.querySelector('meta[name=viewport]');
            const content = viewport.getAttribute('content');

            let zoomBeforeLeaving = window.visualViewport.scale;

            // Handle application visibility changes.
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    // Leaving app, store current state.
                    zoomBeforeLeaving = window.visualViewport.scale;
                }
                else if (document.visibilityState === 'visible') {
                    // Reset temporarily.
                    viewport.setAttribute('content', content + ', maximum-scale=' + zoomBeforeLeaving);
                    setTimeout(() => {
                        viewport.setAttribute('content', content);
                    }, 300);
                }
            });
        }
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {String}                        The string representation of this class.
     */
    toString() {
        return "[core]";
    }

    static toString() {
        return "[core]";
    }

    /** 
     * Identifies the version.
     * @returns {String}                        The version number of this class.
     */
    static version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Add validators to any form element present. And 'tag' labels of required elements with an asterisk.
     * @param {HTMLDocument} container          The document to scan for forms that could/should have validators applied to its children.
     * @param {HTMLFormElement} form            The form to add the validators to, or null to add them to all forms found.
     */
    addValidators(container, form) {
        // When no container is provided, we assume the whole document.
        container ??= document;

        if (form) {
            eventBroadcaster.broadcast(
                {
                    detail: { form: form, validationMessages: resources.validationMessages },
                    message: broadcasts.ADD_VALIDATORS
                });
        }
        else {
            // Add validators to any form present within the container.
            const forms = container.querySelectorAll("form");
            [...forms].forEach((form) => {
                eventBroadcaster.broadcast(
                    {
                        detail: { form: form, validationMessages: resources.validationMessages },
                        message: broadcasts.ADD_VALIDATORS
                    });
            });
        }
    }

    /**
     * Provides an equivalent to window.navigator.serviceWorker.ready that waits for the page to be controlled,
     * as opposed to waiting for the active service worker.
     * See https://github.com/slightlyoff/ServiceWorker/issues/799
     */
    controllerReadyPromise = new Promise(function (resolve) {
        // Resolve with the registration, to match the .ready promise's behavior.
        var resolveWithRegistration = function () {
            window.navigator.serviceWorker.getRegistration().then(function (registration) {
                resolve(registration);
            });
        };

        if (window.navigator.serviceWorker.controller) {
            resolveWithRegistration();
        }
        else {
            window.navigator.serviceWorker.addEventListener("controllerchange", resolveWithRegistration);
        }
    });

    /**
     * Implementation of jQuery's ready() function, so init functions are called even when the 'DOMContentLoaded'' event already fired.
     * @param {Function} fn                 The function to call.
     */
    DOMContentLoaded(fn) {
        if (document.readyState !== "loading") {
            fn();
        }
        else {
            document.addEventListener("DOMContentLoaded", fn);
        }
    }

    /**
     * Download a file through Ajax.
     * @param {String} url                      The url to get the file from.
     * @param {Object} data                     Any form data to send along.
     * @param {Function} callback               Anything to call when done.
     * @param {Boolean} isJson                  Wether the data provided is Json (true) or Form data (false).
     */
    downloadFile(url, data = null, callback = null, isJson = false) {
        var xhr = new XMLHttpRequest();
        xhr.open("POST", url, true);
        xhr.responseType = "blob";
        xhr.onload = function () {
            if (this.status === 200) {
                var blob = this.response;
                var filename = "";
                var disposition = xhr.getResponseHeader("Content-Disposition");
                if (disposition && disposition.indexOf("attachment") !== -1) {
                    var filenameRegex = /filename[^;=\n]*=(([""]).*?\2|[^;\n]*)/;
                    var matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) filename = matches[1].replace(/[""]/g, "");
                }

                if (typeof window.navigator.msSaveBlob !== "undefined") {
                    // IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for which they were created.
                    // These URLs will no longer resolve as the data backing the URL has been freed.
                    window.navigator.msSaveBlob(blob, filename);
                }
                else {
                    var URL = window.URL || window.webkitURL;
                    var downloadUrl = URL.createObjectURL(blob);

                    if (filename) {
                        // Use HTML5 a[download] attribute to specify filename.
                        var a = document.createElement("a");

                        // Safari doesn't support this yet.
                        if (typeof a.download === "undefined") {
                            window.location.href = downloadUrl;
                        }
                        else {
                            a.href = downloadUrl;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                        }
                    }
                    else {
                        window.location.href = downloadUrl;
                    }

                    // Cleanup.
                    setTimeout(function () {
                        URL.revokeObjectURL(downloadUrl);
                    }, this.CLEANUP_URL_TIMEOUT);

                    if (callback) {
                        setTimeout(function () {
                            callback();
                        }, this.CALLBACK_TIMEOUT);
                    }
                }
            }
        };

        if (isJson) {
            xhr.setRequestHeader("Content-type", "application/json");
        }
        else {
            xhr.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
        }

        xhr.send(data);
    }

    /**
     * Handles the click event on the attachment link. Using blob for iOS pwa, default behavior for al the rest (ignored).
     * @param {MouseEvent} event                    The event data for the click.
     */
    async downloadFileBlob(event) {
        // Only apply on iOS PWA environments.
        if (!this.isIosPWA) {
            return;
        }

        event.preventDefault();

        const fileUrl = event.target.getAttribute('href');
        const filename = event.target.textContent.trim();

        await core.fetch({ url: fileUrl})
                  .catch(leanformsNext.markNetworkError)
                  .then(response => response.blob())
                  .then(blob => {
                      // Create a data URL from the blob.
                      const blobUrl = window.URL.createObjectURL(blob);
                  
                      // Create a temporary link and trigger download.
                      const tempLink = document.createElement('a');
                      tempLink.style.display = 'none';
                      tempLink.href = blobUrl;
                      tempLink.setAttribute('download', filename);
                      tempLink.setAttribute('target', '_blank');
                  
                      // Add to DOM, click and remove.
                      document.body.appendChild(tempLink);
                      tempLink.click();
                  
                      // Clean up.
                      setTimeout(() => {
                          window.URL.revokeObjectURL(blobUrl);
                          document.body.removeChild(tempLink);
                      }, 100);
                  })
                  .catch(error => {
                      alert('Download failed:\n' + error);
                  
                      // Fallback -> direct navigation.
                      window.open(url, '_blank');
                  });
    }

    /**
     * Generic fetch with antiforgery token retry.
     * @param {Object} config - Any configuration required, to be merged with the default.
     * @param {String} url - The url of the API to call.
     * @param {Boolean} isRetry - Internal flag to prevent infinite retry loops.
     * @returns {Promise} - A promise with the returned result.
     */
    async fetch({ config = Object.create(null), url = '', isRetry = false }) {
        // Default options are marked with *.
        const defaults = {
            // *Default, no-cache, reload, force-cache, only-if-cached.
            cache: 'no-cache',

            // Include, *same-origin, omit.
            credentials: 'same-origin',

            // *GET, POST, PUT, DELETE, etc.
            method: 'GET',

            // No-cors, cors, *same-origin
            mode: 'cors',

            // Manual, *follow, error.
            redirect: 'follow',

            // No-referrer, *client.
            referrer: 'no-referrer'
        };

        // Combine with defaults.
        const options = { ...defaults, ...config };

        // We need headers - at least for the antiforgeryHeader.
        if (!options.headers) {
            options.headers = {};
        }

        // When posting FormData do not set the content type.
        if (config.body?.constructor.name.localeCompare('FormData', undefined, { sensitivity: 'accent' }) !== 0) {
            // Check if a content-type header is provided in the config.
            if (!options.headers['Content-Type']) {
                options.headers['Content-Type'] = 'application/json; charset=utf-8';
            }
        }

        // Don't add token to GET requests.
        if (options.method.toUpperCase() !== 'GET') {
            options.headers[antiforgeryHeaderName] = this.getAntiForgeryToken();
        }

        try {
            const response = await fetch(url, options);

            // Handle 400 errors that might be due to antiforgery token issues.
            if (response.status === 400 && !isRetry && options.method.toUpperCase() !== 'GET') {
                // Try to get a fresh token.
                await this.updateAntiForgeryToken();

                // Retry the request once with the new token.
                return await this.fetch({
                    config,
                    url,
                    isRetry: true
                });
            }

            // Return an empty string when 'no-content' (204) is returned.
            if (response.status === 204) {
                return '';
            }

            // Return the response for the implementer to deal with.
            return response;
        }
        catch (error) {
            // Re-throw any other errors.
            throw error;
        }
    }

    /**
     * Retrieves the antiforgery token from various possible sources.
     * @returns {string|null} - The antiforgery token if found, otherwise null.
     */
    getAntiForgeryToken() {
        // If the token is already cached on the instance, return it immediately.
        if (this.antiForgeryToken) {
            return this.antiForgeryToken;
        }

        // Possible sources for the antiforgery token.
        const tokenSources = [
            parent.core?.antiForgeryToken,
            top.core?.antiForgeryToken,
            document.querySelector('input[name="RequestVerificationToken"]')?.value,
            parent?.document.querySelector('input[name="RequestVerificationToken"]')?.value,
            top.document.querySelector('input[name="__RequestVerificationToken"]')?.value
        ];

        // Iterate through sources to find the token.
        for (const source of tokenSources) {
            if (source) {
                this.antiForgeryToken = source;

                break;
            }
        }

        return this.antiForgeryToken;
    }

    /**
     * Gets the URL of the embedded page, from the parent URL.
     * @returns {URL}                           The URL object for the embedded page.
     */
    getEmbedUrl() {
        const pageUrlParameter = decodeURIComponent(this.getQueryParameter("url"));

        return new URL(pageUrlParameter);
    }

    /**
     * Get's the icon to display for a given filename.
     * @param {String} fileName
     */
    getFileIconSuffix(fileName) {
        if (fileName && fileName.indexOf(".") > -1) {
            // Also used by regular attachments.
            const knownExtensions = ["doc", "docx", "pdf", "xls", "xlsx"];

            const extension = fileName.split(".").pop().toLowerCase();
            const isKnownExtension = knownExtensions.indexOf(extension) > -1;

            if (isKnownExtension) {
                return extension.substring(0, 3) + ' custom';
            }
        }

        return "draft";
    }

    /**
     * Create from data.
     * @param {HTMLFormElement} form            The form to get the form data for
     * @returns {FormData}                      The complete formdata object for the given form
     */
    getFormDataWithDisabled(form) {
        const formData = new FormData(form);
        const disabledElements = form.querySelectorAll(":disabled");
        [...disabledElements].forEach(element => {
            if (element.type === "checkbox" || element.type === "radio") {
                if (element.checked) {
                    formData.append(element.name, element.value);
                }
            }
            else if (element.type === "select-multiple") {
                // Handle multi-select options.
                [...element.options].forEach(option => {
                    if (option.selected) {
                        formData.append(element.name, option.value);
                    }
                });
            }
            else {
                // Append other disabled fields.
                formData.append(element.name, element.value);
            }
        });

        return formData;
    }

    /**
     * Gets the value of a provided parameter from a querystring, handles complex urls too.
     * @param {String} parameter                The querystring parameter to look for.
     * @param {Window} reference                The window to get the location from.
     * @returns {String}                        The value for the requested param.
     */
    getQueryParameter(parameter, reference = top.window) {
        const params = new Proxy(this.#parseUrl(reference.location.href).params, {
            get: (searchParams, prop) => searchParams.get(prop),
        });

        return params[parameter];
    }

    /**
     * Gets the 'page' parameter in a given URL.
     * @param {Window} reference                The window to get the location from.
     * @returns {String}                        The value for the requested param.
     */
    getPageQueryParameter(reference = top.window) {
        const params = new Proxy(this.#parseUrl(reference.location.href, true).params, {
            get: (searchParams, prop) => searchParams.get(prop),
        });

        return params["page"];
    }

    /**
     * Hides a toast notification.
     * @param {Object} notification             The notification.
     */
    hideNotification(notification) {
        this.#toast.hide(notification);
    }

    /**
     * Decode a HTML encoded string back to Html (safe - no script is evaluated).
     * @param {String} input                    The content to decode.
     */
    htmlDecode(input) {
        var doc = new DOMParser().parseFromString(input, "text/html");

        return doc.documentElement.textContent;
    }

    /**
     * Use this function to encode text for use as HTML element content (not attribute or elsewhere)
     * 
     * Using .textContent directly is preferred.
     * If moving away from HTML generation with strings is not reasonable, use this function.
     * 
     * @param {string} htmlstring               Text that should be HTML encoded
     */
    htmlEncode(htmlstring) {
        let el = document.createElement("p");
        el.textContent = htmlstring;

        return el.innerHTML;
    }

    /**
     * Checks if a given element is visible within the viewport.
     * @param {HTMLELement} element             The element to validate against.
     * @param {Integer} offset                  How many pixels to use as a buffer for the position check (take into account a fixed header par example).
     * @param {HTMLELement} container           The container that contains the element, defaults to document.documentElement.
     * @returns                                 True when the element is visible, false if not.
     */
    isElementInView(element, offset = 0, container = document.documentElement) {
        const rect = element.getBoundingClientRect();

        return rect.top >= offset &&
            rect.left >= offset &&
            rect.bottom <= container.clientHeight - offset &&
            rect.right <= container.clientWidth - offset;
    }

    /**
     * Checks if a given element is visble.
     * @param {HTMLELement} element             The element to validate against.
     * @returns                                 True when the element is visible, false if not.
     */
    isVisible(element) {
        return !!(element?.offsetWidth || element?.offsetHeight || element?.getClientRects?.().length)
            && window.getComputedStyle(element).visibility !== "hidden";
    }

    /**
     * Displays a toast notification.
     * @param {String} type - The message type; info, success, warn, error. Defaults to 'warn'.
     * @param {String} message - The message text to display.
     * @param {Object} options - [Optional] Any additional settings to pass on to the toast instance.
     * @returns {Object} - The toast notification instance.
     */
    notify(type, message, options) {

        // Find the top most window that has this function defined.
        try {
            if (window.parent !== window && window.parent.core?.notify) {
                return window.parent.core.notify(type, message, options);
            }
        }
        catch {
            // Ignore cross-origin errors.
        }

        options = options || {};

        if (!options.position) {
            const currentWidth = window.innerWidth;
            options.position = currentWidth >= this.BREAKPOINT ? toast.toastPosition.TopCenter : toast.toastPosition.BottomCenter;
        }

        // Set default to call.
        type = typeof this.#toast[type] === "function" ? type : "warn";
        return this.#toast[type](message ?? resources.validationMessages["default"], options);
    }

    /**
     * Parses a dimension value , independent of value type.
     * @param {String} value                    The value to parse.
     */
    parseDimensionValue(value) {
        const parsed = parseFloat(value);
        if (isNaN(parsed) || !value) {
            return 0;
        }

        if (value.toString().indexOf("em") !== -1) {
            return parsed * this.#rem;
        }

        return parsed;
    }

    /**
     * Retruns a CSS custom property value applied at the element.
     * Like: core.readCssVar("--color", document.querySelector(".box"));
     * @param {String} varName                  The name of the variable to get the value of.
     * @param {DOMElement} element              [Optional] The reference element. Defaults to document.documentElement.
     */
    /* eslint-disable-next-line class-methods-use-this -- Cannot use/access static methods within an anonymous class expression.*/
    readCssVar(varName, element = document.documentElement) {
        const elementStyles = window.getComputedStyle(element);
        const value = elementStyles.getPropertyValue(`${varName}`).trim();

        return value.startsWith("\"") ? value.substring(1, value.length - 1) : value;
    }

    /**
     * Removes the given parameter.
     * @param {String} url                      The url that the query string parameter should be removed.
     * @param {String} parameter                The parameter to remove. 
     */
    removeQueryParameter(url, parameter) {
        return this.setQueryParameter(url, parameter, null);
    }

    /**
     * Rendering potentially untrusted HTML in a safe manner.
     * @param {string} html                     The string HTML to sanitize.
     */
    sanitizeHtml(html, allowedTags = []) {
        let sanitized = html;
        if (allowedTags.length > 0) {
            sanitized = DOMPurify.sanitize(html,
                {
                    ADD_ATTR: ["target"],
                    ALLOWED_TAGS: allowedTags
                });
        }
        else {
            sanitized = DOMPurify.sanitize(html,
                {
                    USE_PROFILES:
                    {
                        html: true
                    },
                    ADD_ATTR: ["target"]
                });
        }
        
        return sanitized;
    }

    /**
     * Sets the antiforgery token.
     * @param {String} token - The token.
     */
    setAntiForgeryToken(token) {
        if (parent.core?.antiForgeryToken) {
            parent.core.antiForgeryToken = token;

            const parentInput = parent?.document?.querySelector('input[name="__RequestVerificationToken"]');

            if (parentInput) {
                parentInput.value = token;
            }
        }

        this.antiForgeryToken = token;

        const input = document.querySelector('input[name="__RequestVerificationToken"]');

        if (input) {
            input.value = token;
        }
    }

    /**
     * Replaces or adds the given parameter with its corresponding value.
     * The method only looks at the last '?' for indicating the search params, so this will also work in complex urls.
     * @param {String} url                      The url that needs it query string parameters modified.
     * @param {String} parameter                The parameter to replace or add. 
     * @param {Object} value                    The value for the parameter.
     */
    setQueryParameter(url, parameter, value) {
        if (url === "about:blank" || url === "") {
            return url;
        }

        // Get url parts.
        const urlObject = this.#parseUrl(url);
        const path = urlObject.path;
        const urlSearchParams = urlObject.params;

        // Remove parameter when value is null.
        if (value === null) {
            urlSearchParams.delete(parameter);
        }
        else {
            urlSearchParams.set(parameter, value);
        }

        const outputParams = [];

        for (let [key, value] of urlSearchParams.entries()) {
            if (key !== "page") {
                value = encodeURIComponent(value);
            }

            outputParams.push(`${key}=${value}`);
        }

        // Join again.
        return `${path}${outputParams.length ? "?" + outputParams.join("&") : ""}`;
    }
    
    /**
     * Sets the URL of the embedded page to the parent URL.
     * @param {URL} url                      The embedded url should be set to the parent.
     * @param {Boolean} url                  True to use pushState, false to use replaceState.
     */
    setEmbedUrl(url, pushState = false) {
        const parentUrl = new URL(top.window.location);
        parentUrl.searchParams.set("url", url);
        
        const historyFunction = pushState ? "pushState" : "replaceState";
        top.window.history[historyFunction](null, "", parentUrl);
    }

    /**
     * Returns true when the current browser supports offline behavior.
     */
    supportsOffline() {
        // Not every feature is tested, only those that are easy to check.
        const supportServiceWorker = "serviceWorker" in navigator;
        const supportsFetch = "fetch" in window;
        const supportsPromise = "Promise" in window;
        const supportsSearchParams = "URLSearchParams" in window;

        return [
            supportServiceWorker,
            supportsFetch,
            supportsPromise,
            supportsSearchParams
        ].every((feature) => {
            return feature === true;
        });
    }

    /**
     * Toggles the visibility of the element.
     * Ignore when no element is given.
     * @param {HTMLElement} el                  The element to toggle its display state for.
     */
    toggleDisplayState(el) {
        if (el) {
            if (el.style.display === "none") {
                el.style.display = "";
            }
            else {
                el.style.display = "none";
            }
        }
    }

    /**
    * Refreshes the antiforgery token, i.e. get's the current token from the server.
    * @returns {Promise<void>}
    */
    async updateAntiForgeryToken() {
        const response = await fetch(`${baseUrl}/api/antiforgery`);
        const data = await response.json();

        this.setAntiForgeryToken(data.token);
    }

    /**
    * Decode s aprovided string (back) to valid xml.
    * @param {String} str                       The string to decode to proper xml.
    */
    xmlDecode(str) {
        if (!str) {
            return "";
        }

        return str.replace(/&apos;/g, "'").
            replace(/&quot;/g, "\"").
            replace(/&gt;/g, ">").
            replace(/&lt;/g, "<").
            replace(/&amp;/g, "&");
    }

    /**
     * Encode a string of xml to something that we can safely send over https.
     * @param {String} str                      The xml to encode.
     */
    xmlEncode(str) {
        if (!str) {
            return "";
        }

        return str.replace(/&/g, "&amp;").
            replace(/</g, "&lt;").
            replace(/>/g, "&gt;").
            replace(/"/g, "&quot;").
            replace(/'/g, "&apos;");
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Parses the url parameters, including complex urls.
     * @param {String} url                      The url string to parse the urls parameters for.
     * @param {Boolean} getPage                 When we want the full page querystring parameter, as ReturnUrl par example. Defaults to false.
     * @returns {Object}                        Contains the path and URLSearchParams.
     */
    #parseUrl(url, getPage = false) {
        const viewIndex = url.lastIndexOf("?page");

        if (getPage) {

            return {
                path: url.slice(0, viewIndex),
                params: new URLSearchParams(url.slice(viewIndex))
            }
        }
        
        const index = url.lastIndexOf("?");
        var isEmptyComplexurl = false;
        if (viewIndex === index) {
            // We have a complex url without a queryParameter for the page url.
            isEmptyComplexurl = true;
        }

        const path = isEmptyComplexurl ? url : url.slice(0, index);
        const searchParams = isEmptyComplexurl ? "" : url.slice(index);

        return {
            path: path,
            params: new URLSearchParams(searchParams)
        }
    }
    // #endregion ********************************************************************************/

}();
// #endregion ************************************************************************************/
